import { Fragment, ReactNode, useState } from 'react';

import { TFunction } from 'i18next';
import uniqBy from 'lodash-es/uniqBy';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import {
  aliasLabel,
  prettyProvider,
} from '@/modules/aiAgents/components/ModelStrategy/ModelPicker';
import {
  findCatalogEntry,
  useModelStrategyCatalog,
} from '@/modules/aiAgents/hooks/useModelStrategyCatalog';
import { ModelStrategyStep } from '@/shared/types/modelStrategy';
import { ContextChangeType, DebugStep, Entity } from '@/shared/types/tryIt';
import { capitalizeFirstLetter } from '@/util/util';

import { webhookSeverity } from './buildTurnIssues';
import {
  CONTEXT_CHANGE_LABEL_KEY,
  ExpandableRow,
  NewTabStepLink,
  PlainRow,
  TooltipStepLink,
} from './DetailsRow';
import { useConditionLink } from '../../../hooks/useConditionLink';
import { useNodeName } from '../../../hooks/useDialogsList';
import { useGoToDialogAction } from '../../../hooks/useGoToDialogAction';
import { useGoToDialogNode } from '../../../hooks/useGoToDialogNode';
import { useGoToEntity } from '../../../hooks/useGoToEntity';
import { useRequisiteSaveAs } from '../../../hooks/useRequisiteSaveAs';
import { useGoToWebhookPage, useWebhookName } from '../../../hooks/useWebhookLink';
import { selectBrainId } from '../../../redux/selectors';

/** The copy keeps its `{{0}}` slot for the legacy panel, so we split on a sentinel to wrap the name. */
export const NAME_SLOT = '\u0000';

export const DialogNodeLink = ({ nodeId, children }: { nodeId?: string; children: ReactNode }) => {
  const { t } = useTranslation();
  const node = useGoToDialogNode(nodeId);

  return (
    <TooltipStepLink link={node ?? null} tooltip={t('try_it.go_to_dialog_node')}>
      {children}
    </TooltipStepLink>
  );
};

export const NodeName = ({ nodeId, stackName }: { nodeId?: string; stackName?: string }) => {
  const resolved = useNodeName(nodeId);

  return <>{stackName ?? resolved ?? nodeId}</>;
};

export const McpToolStepHead = ({ step }: { step: DebugStep }) => {
  const { t } = useTranslation();
  const [before, after = ''] = t('try_it.details.tool_invocation', { 0: NAME_SLOT }).split(
    NAME_SLOT
  );

  return (
    <PlainRow>
      {before}
      <span className="text-sm leading-none font-medium text-foreground">{step.mcp_tool_name}</span>
      {after}
      {step.duration !== undefined && ` · ${t('try_it.details.duration_ms', { 0: step.duration })}`}
    </PlainRow>
  );
};

/** `null` while the catalog loads, and for a user who cannot read the model strategy. */
export const useAnswerModelLabel = (step: ModelStrategyStep | undefined): string | null => {
  const { t } = useTranslation();
  const brainId = useSelector(selectBrainId);
  const { buckets } = useModelStrategyCatalog(brainId);

  const entry = step && findCatalogEntry(buckets, step);
  const provider = prettyProvider(entry?.provider, t);
  const name = entry ? aliasLabel(entry.alias, t) : (step?.language_model_alias ?? null);

  if (!name) {
    return null;
  }

  return provider ? `${provider} · ${name}` : name;
};

export const AnswerModelHead = ({ label }: { label: string }) => {
  const { t } = useTranslation();

  return (
    <PlainRow>
      {t('try_it.details.answer_generated_by')}{' '}
      <span className="font-medium text-foreground">{label}</span>
    </PlainRow>
  );
};

export const AIResponseStepHead = ({ step }: { step: DebugStep }) => {
  const { t } = useTranslation();
  const action = useGoToDialogAction(step.action_id, step.node_id);
  const node = useGoToDialogNode(step.node_id);
  const link = action ?? node;

  return (
    <PlainRow>
      {t('try_it.details.ai_response_generated')}{' '}
      <TooltipStepLink link={link ?? null} tooltip={t('try_it.go_to_response')}>
        {t('actions.types.ai_response')}
      </TooltipStepLink>
    </PlainRow>
  );
};

export const EntitiesHead = ({ entities }: { entities: Entity[] }) => {
  const { t } = useTranslation();
  const entityLink = useGoToEntity();
  const unique = uniqBy(entities, 'entity');

  return (
    <PlainRow>
      {t('try_it.entity', { count: unique.length })}{' '}
      {unique.map((entity, index) => (
        <Fragment key={entity.entity}>
          <TooltipStepLink
            link={entityLink ? { to: entityLink.to(entity.entity) } : null}
            tooltip={t('try_it.details.go_to_entity')}
          >
            {entity.entity}
          </TooltipStepLink>
          {index < unique.length - 1 && ', '}
        </Fragment>
      ))}{' '}
      {t('try_it.matched')}
    </PlainRow>
  );
};

export const ConditionStepHead = ({ step }: { step: DebugStep }) => {
  const { t } = useTranslation();
  const condition = useConditionLink(step.condition_id);

  return (
    <PlainRow>
      {t('actions.types.condition')}{' '}
      <TooltipStepLink link={condition ?? null} tooltip={t('try_it.details.go_to_condition')}>
        {condition?.name}
      </TooltipStepLink>{' '}
      {t('try_it.details.was_true')}
    </PlainRow>
  );
};

const shortReason = (message: string) => {
  const firstSentence = message
    .split('.')[0]
    .trim()
    .replace(/^webhook\s+/i, '');
  return firstSentence.charAt(0).toLowerCase() + firstSentence.slice(1);
};

/** The message and details name their subject ("customer not found"); the bare code does not. */
export const webhookOutcome = (step: DebugStep, t: TFunction): string | null => {
  if (!step.webhook_error) {
    return null;
  }

  const fallbackSent = (step.sub_steps ?? []).some((sub) => sub.action_type === 'text');
  if (fallbackSent) {
    return t('try_it.turn_issues.fallback_message_sent');
  }

  const { message, details, code } = step.webhook_error;
  const reported = message?.trim() || details?.trim();
  if (reported) {
    return shortReason(reported);
  }

  return code?.replace(/[-_]/g, ' ').toLowerCase() || null;
};

export const WebhookStepHead = ({ step }: { step: DebugStep }) => {
  const { t } = useTranslation();
  const name = useWebhookName(step.webhook_id);
  const page = useGoToWebhookPage(step.webhook_id);
  const outcome = webhookOutcome(step, t);

  return (
    <PlainRow>
      {t('actions.types.webhook')}{' '}
      <NewTabStepLink to={page?.to ?? null} tooltip={t('try_it.details.go_to_webhook')}>
        {name ?? step.webhook_id}
      </NewTabStepLink>{' '}
      {t('try_it.details.called')}
      {outcome && ` · ${outcome}`}
    </PlainRow>
  );
};

export const outcomeIconClass = (succeeded: boolean) =>
  succeeded ? '[&_svg]:text-text-success-firm' : '[&_svg]:text-destructive';

export const OutcomeBadge = ({
  flavour = 'neutral',
  className,
  children,
}: {
  flavour?: 'informational' | 'successful' | 'warning' | 'destructive' | 'neutral';
  className?: string;
  children: ReactNode;
}) => (
  <Badge
    type="outline"
    flavour={flavour}
    size="xs"
    rounded={false}
    className={cn(
      'bg-background leading-4 font-semibold',
      flavour === 'successful' && 'text-text-success-firm',
      className
    )}
  >
    {children}
  </Badge>
);

const WEBHOOK_SUCCESS_STATUS = 200;

/** Webhook outcome badges. The orchestrator reports a status code only on failure, so a call that
 * succeeded reads as {@link WEBHOOK_SUCCESS_STATUS}. */
export const WebhookStatusPills = ({ step }: { step: DebugStep }) => {
  const { t } = useTranslation();
  const statusCode = step.webhook_error?.status_code;
  const severity = webhookSeverity(step);

  if (step.duration === undefined && !severity) {
    return null;
  }

  return (
    <div className="flex items-center gap-1.5">
      {severity ? (
        <OutcomeBadge flavour="destructive">
          {statusCode === undefined
            ? t('try_it.details.failed')
            : t('try_it.details.status_code', { 0: statusCode })}
        </OutcomeBadge>
      ) : (
        <OutcomeBadge flavour="successful">
          {t('try_it.details.status_code', { 0: statusCode ?? WEBHOOK_SUCCESS_STATUS })}
        </OutcomeBadge>
      )}
      {step.duration !== undefined && (
        <OutcomeBadge className="border-border">
          {t('try_it.details.execution_time', { 0: step.duration })}
        </OutcomeBadge>
      )}
    </div>
  );
};

const CONTEXT_CHANGE_CLASS: Record<ContextChangeType, string> = {
  [ContextChangeType.UPDATED]: 'text-informational',
  [ContextChangeType.ADDED]: 'text-text-success-firm',
  [ContextChangeType.REMOVED]: 'text-destructive',
};

const formatValue = (value: unknown): string => {
  if (value === undefined || value === null) {
    return '';
  }
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

const WebhookFailureNote = ({ step }: { step: DebugStep }) => {
  const { code, details } = step.webhook_error ?? {};

  if (!code && !details) {
    return null;
  }

  return (
    <span className="line-clamp-1 max-w-sm text-sm leading-5 font-normal text-foreground">
      <span className="text-destructive">{code}</span>
      {code && details && ' · '}
      {details}
    </span>
  );
};

const flattenSteps = (steps: DebugStep[]): DebugStep[] =>
  steps.flatMap((step) => [step, ...flattenSteps(step.sub_steps ?? [])]);

export const overwrittenAfter = (steps: DebugStep[], step: DebugStep): Set<string> => {
  const flat = flattenSteps(steps);
  const index = flat.indexOf(step);
  const later = index === -1 ? [] : flat.slice(index + 1);

  return new Set(later.flatMap((other) => Object.keys(other.context_changes ?? {})));
};

export const WebhookContextCard = ({
  step,
  steps = [],
  context,
  subject,
}: {
  step: DebugStep;
  steps?: DebugStep[];
  context?: Record<string, unknown>;
  subject?: string;
}) => {
  const { t } = useTranslation();
  const name = useWebhookName(step.webhook_id);
  const [showJson, setShowJson] = useState(false);

  const entries = Object.entries(step.context_changes ?? {});
  if (entries.length === 0 && !step.webhook_error) {
    return null;
  }

  const overwritten = overwrittenAfter(steps, step);
  const changed = Object.fromEntries(
    entries.map(([name]) => [name, overwritten.has(name) ? undefined : context?.[name]])
  );
  // The call's own shape: what it set, and what went wrong. A clean call reports `null`.
  const payload = { context: changed, error: step.webhook_error ?? null };

  return (
    <div className="flex flex-col items-start gap-2 self-stretch">
      <div className="flex items-center justify-between self-stretch rounded-md border border-stroke bg-bg-muted py-2 pr-2 pl-4">
        <span className="text-sm leading-5 font-medium text-foreground">
          {capitalizeFirstLetter(t('common.context_variables'))}
        </span>
        <div className="flex items-center gap-2 p-0">
          <span className="text-sm leading-none font-medium text-foreground">
            {t('common.view_json')}
          </span>
          <Switch
            checked={showJson}
            onCheckedChange={setShowJson}
            aria-label={t('try_it.details.view_json_for', {
              0: name ?? subject ?? step.webhook_id,
            })}
          />
        </div>
      </div>

      <div className="flex flex-col items-start gap-4 self-stretch rounded-md border border-stroke bg-background p-4">
        {showJson ? (
          <pre className="w-full overflow-x-auto text-xs leading-5 text-foreground-muted">
            {JSON.stringify(payload, null, 2)}
          </pre>
        ) : (
          <>
            {step.webhook_error && <WebhookFailureNote step={step} />}
            {entries.map(([name, change]) => (
              <div key={name} className="flex flex-col items-start gap-0.5 self-stretch">
                <div className="flex items-center gap-3 self-stretch">
                  <span className="line-clamp-1 text-sm leading-none font-medium text-foreground">
                    {name}
                  </span>
                  <span
                    className={cn(
                      'line-clamp-1 shrink-0 text-sm leading-none font-medium',
                      CONTEXT_CHANGE_CLASS[change]
                    )}
                  >
                    {capitalizeFirstLetter(t(CONTEXT_CHANGE_LABEL_KEY[change]))}
                  </span>
                </div>
                {!overwritten.has(name) && (
                  <span className="h-5 max-w-sm self-stretch truncate text-xs leading-5 font-normal text-foreground-muted">
                    {formatValue(changed[name])}
                  </span>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export const WebhookStepRow = ({
  step,
  hasNested = false,
  children,
}: {
  step: DebugStep;
  hasNested?: boolean;
  children?: ReactNode;
}) => {
  const { t } = useTranslation();

  // `children` is always a non-empty array, so ask the step what it has.
  const hasInfo =
    step.webhook_error !== undefined ||
    step.duration !== undefined ||
    Object.keys(step.context_changes ?? {}).length > 0 ||
    hasNested;

  return (
    <ExpandableRow
      head={<WebhookStepHead step={step} />}
      label={t('try_it.details.show_call_details')}
      detail={
        hasInfo && (
          <div className="flex flex-col items-start gap-2 self-stretch pb-2">
            <WebhookStatusPills step={step} />
            {children}
          </div>
        )
      }
    />
  );
};

export const TagStepHead = ({ step }: { step: DebugStep }) => {
  const { t } = useTranslation();

  if (step.tag_operation === 'clear') {
    return <PlainRow>{t('try_it.details.tags_cleared')}</PlainRow>;
  }

  const tags = step.tags ?? [];
  const sentence =
    step.tag_operation === 'remove'
      ? t('try_it.details.tags_removed', { count: tags.length, 0: NAME_SLOT })
      : t('try_it.details.tags_applied', { count: tags.length, 0: NAME_SLOT });
  const [before, after = ''] = sentence.split(NAME_SLOT);

  return (
    <PlainRow>
      {before}
      {tags.map((tag, index) => (
        <Fragment key={tag}>
          <span className="text-sm leading-none font-medium text-foreground">{tag}</span>
          {index < tags.length - 1 && ', '}
        </Fragment>
      ))}
      {after}
    </PlainRow>
  );
};

export const JumpStepHead = ({ step, nodeName }: { step: DebugStep; nodeName?: string }) => {
  const { t } = useTranslation();

  return (
    <PlainRow>
      {t('actions.types.jump_to')}{' '}
      <DialogNodeLink nodeId={step.node_id}>
        <NodeName nodeId={step.node_id} stackName={nodeName} />
      </DialogNodeLink>{' '}
      {t('dialog.jump_to.label')}
    </PlainRow>
  );
};

export const QuestionStepHead = ({ step }: { step: DebugStep }) => {
  const { t } = useTranslation();
  const saveAs = useRequisiteSaveAs(step.requisite_id);

  return (
    <PlainRow>
      {t('try_it.details.question_to_fill_in')}{' '}
      <span className="font-medium text-foreground">${saveAs}</span>{' '}
      {t('try_it.details.value_asked')}
    </PlainRow>
  );
};
